import React, { type JSX } from 'react'
import styled, { css } from 'styled-components'
import { primaryColor } from 'uiComponents/constants/colors'
import {
  fontFamilyPrimary,
  fwMediumBold,
} from 'uiComponents/constants/typography'
import Emoji, { EmojiType } from '../Emoji/Emoji'
import { getBackgroundColor, getFontColor } from './helpers'

interface IBadge {
  title: string
  backgroundColor?: string
  isSale?: boolean
  isExtraSale?: boolean
  className?: string
  iconName?: EmojiType
  padded?: boolean
  dataTestId?: string
  border?: boolean
  as?: keyof JSX.IntrinsicElements | React.ComponentType<any>
}

interface IEmojiContainer {
  type?: string
}

interface IBadgeProps {
  backgroundColor: string
  padded?: boolean
  fontColor: string
  className?: string
  border?: boolean
}

const BadgeContainer = styled.div<Partial<IBadgeProps>>`
  display: inline-block;
  background: ${props => props.backgroundColor};
  border-radius: 2px;
  ${fontFamilyPrimary};
  font-size: 11px;
  font-size: var(--badge-font-size, 11px);
  ${fwMediumBold};
  line-height: 16px;
  padding: 4px 8px;
  color: ${props => props.fontColor};
  white-space: nowrap;
  direction: initial;

  ${({ padded }) =>
    padded
      ? css`
          padding: 4px 8px 4px 15px;
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        `
      : null};

  ${({ border }) =>
    border &&
    css`
      border: 1px solid ${primaryColor};
      padding: 3px 8px;
    `};
`

const EmojiContainer = styled.span<IEmojiContainer>`
  display: inline-block;
  margin-right: 2px;
  margin-left: -2px;
`

const Badge = ({
  iconName,
  title,
  backgroundColor,
  isSale,
  className,
  isExtraSale,
  padded,
  dataTestId,
  border,
  as,
}: IBadge) => {
  const finalBackground = getBackgroundColor({
    backgroundColor,
    isSale,
    isExtraSale,
  })
  const finalFontColor = getFontColor({
    backgroundColor,
    isSale,
    isExtraSale,
  })

  const getDataTestId = () => {
    if (dataTestId) {
      return dataTestId
    } else if (iconName) {
      return 'IconBadge'
    } else if (isSale) {
      return 'SaleBadge'
    } else if (isExtraSale) {
      return 'isExtraSaleBadge'
    }
    return 'Badge'
  }

  return (
    <BadgeContainer
      data-testid={getDataTestId()}
      className={className}
      as={as}
      backgroundColor={finalBackground}
      padded={padded}
      fontColor={finalFontColor}
      border={border}
    >
      {iconName && <EmojiContainer as={Emoji} type={iconName} />}
      {title}
    </BadgeContainer>
  )
}

export default Badge
